import withEvents from 'analytics/withEvents';
import Select from 'ds/components/input/Select';
import { State as RouteState, NavigateToFn, Route } from 'config/routes';
import { URPotential } from 'config/abTests';
import { useLocale } from 'locale';
import { noop } from 'lodash';
import React from 'react';
import { SorterWrapper } from 'ds/components/Layout';
import { config as sortConfig } from './config';
import { SortDirection, SortField, SortValue } from './types';
import { makeSet, stringify } from './utils';
import styled from '@emotion/styled';
import { CommutePopupStatus } from 'components/commute-popup/types';
import { connect } from 'react-redux';
import { State, RootAction } from 'store/state';
import { Dispatch, bindActionCreators } from 'redux';
import { setCommutePopupStatus } from 'store/state/app/actions';
import { commutePopupStatusSelector } from 'store/state/app/selectors';
import { routeSelector } from 'store/state/selectors/router';
import * as actions from 'store/state/router/actions';
import SortIcon from 'assets/svg/sort.svg';
import { DealType, MarketplaceType } from 'utils/entities';
import { searchRouteByMarketplace } from 'utils/marketplaceRoutes';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useABTests } from 'hooks/useABTests';

const InlineMargin = styled.span`
  margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(1)};
`;

const valueComparator = (value1: SortValue, value2: SortValue) => stringify(value1) === stringify(value2);

interface SortSectionProps {
  onAfterSelectChange?: (value: SortValue) => void;
  commutePopupStatus: CommutePopupStatus;
  onShowCommutePopup: () => void;
  navigate: NavigateToFn;
  route: RouteState;
  noCounter?: boolean;
  marketplace: MarketplaceType;
  rightIcon?: React.ReactNode;
  customIcon?: React.ReactNode;
}

const SortSection: React.FC<SortSectionProps> = (props) => {
  const {
    onAfterSelectChange = noop,
    navigate,
    route,
    marketplace,
    noCounter = false,
    rightIcon,
    customIcon,
  } = props;

  const currentBreakpoint = useScreenBreakpoint();
  const isMobile = currentBreakpoint <= 2;
  const isInvestors = route.name === Route.InvestorsSearchPage;
  const { urPotentialMode } = useABTests();

  const setValues = (sortValues: SortValue[]) => {
    navigate(isInvestors ? Route.InvestorsSearchPage : searchRouteByMarketplace(marketplace), {
      ...route.params,
      dealType: isInvestors ? DealType.Buy : route.params.dealType,
      sort: sortValues.filter(Boolean),
      page: undefined,
      tracking_search_source: 'sort_by',
    });
  };

  let { defaultValue, basicOptions } = sortConfig;

  if (isInvestors) {
    defaultValue = [ [ SortField.RentYield, SortDirection.Desc ] ];
    basicOptions = [
      [ SortField.Price, SortDirection.Asc ],
      [ SortField.Date, SortDirection.Desc ],
      [ SortField.RentYield, SortDirection.Desc ],
      ...(urPotentialMode === URPotential.modeB ? [ [ SortField.UrbanRenewal, SortDirection.Desc ] as SortValue ] : []),
    ];
  }

  const selectChangeHandler = (value: SortValue) => {
    onAfterSelectChange(value);

    const sortValues = makeSet(route.params.sort);
    basicOptions.forEach((opt) => {
      sortValues.delete(opt);
    });
    setValues([ value, ...sortValues.values() ]);
  };

  const { t } = useLocale();

  const options = basicOptions.map((opt) => ({
    value: opt,
    label: t('listingSort.optionLabel', { value: stringify(opt) }),
  }));

  const values = makeSet(route.params.sort);
  const isAdvancedModeActive = values.size === 0 || !basicOptions.some((value) => values.has(value));

  const [ selectValue ] = isAdvancedModeActive ? defaultValue : route.params.sort;
  const selectItemCountLabel = isAdvancedModeActive && values.size ? values.size : undefined;

  return (
    <>
      <SorterWrapper>
        <Select
          selectionPromptText={t('listingSort.selectButtonLabel')}
          renderSelected={label => (
            <>
              <span>{t('listingSort.selectButtonLabel')}</span>
              <InlineMargin>{` ${label} ${noCounter ? '' : (selectItemCountLabel === undefined ? '' : `(${selectItemCountLabel})`)}`}</InlineMargin>
            </>
          )}
          items={options}
          value={selectValue}
          onChange={selectChangeHandler}
          valueExtractor={(item => item.value)}
          valueComparator={valueComparator}
          data-auto="sort-options"
          customIcon={customIcon || (isMobile ? <></> : undefined)}
          rightIcon={rightIcon || <SortIcon/>}
          noBorders
        />
      </SorterWrapper>
    </>
  );
};

const mapStateToProps = (state: State) => ({
  commutePopupStatus: commutePopupStatusSelector(state),
  route: routeSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  navigate: bindActionCreators(actions.navigateTo, dispatch),
  onShowCommutePopup: () => dispatch(setCommutePopupStatus(CommutePopupStatus.Open)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withEvents<SortSectionProps>((sendEvent) => ({
  onAfterSelectChange(value: SortValue) {
    sendEvent('search_sort_click', 'search', { event: {
      sort_by_button_selected: value ? value[0] : 'power',
    }});
  },

}))(SortSection));
